import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import { getNextWizardStepAtom } from "@/features/BookingWizard/atoms/selectors/getNextWizardStepAtom/getNextWizardStepAtom";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import { navigateToNextStepAtom } from "../../../../atoms/actions/navigateToNextStep/navigateToNextStepAtom";
import {
  BookingWizardDataAtom,
  CurrentBookingWizardStepAtom,
  CurrentRequestGroupIndexAtom,
  ListOfBookingWizardStepsAtom,
  isCreatingBookingAtom,
} from "../../../../atoms/stores";
import { useCompleteBooking } from "../useCompleteBooking/useCompleteBooking";

const isFinalStepAtom = atom<boolean>((get) => {
  const steps = get(ListOfBookingWizardStepsAtom);
  const currentStep = get(CurrentBookingWizardStepAtom);
  const bookingWizardData = get(BookingWizardDataAtom);
  const currentRequestGroupIndex = get(CurrentRequestGroupIndexAtom);

  const stepIndex = steps?.findIndex((step) => step === currentStep);

  const requestGroupCount =
    bookingWizardData?.type === "RequestBooking"
      ? bookingWizardData.requestGroupCount ?? 1
      : 1;
  const hasRemainingRequestGroup =
    bookingWizardData?.type === "RequestBooking" &&
    requestGroupCount > 1 &&
    ["AddPlayers", "AddPlayersPrivate"].includes(currentStep ?? "") &&
    currentRequestGroupIndex < requestGroupCount - 1;

  return stepIndex === steps?.length - 1 && !hasRemainingRequestGroup;
});

type Props = {
  onClose: () => void;
};

export const usePressContinue = ({ onClose }: Props) => {
  const navigateToNextStep = useSetAtom(navigateToNextStepAtom);
  const nextStep = useAtomValue(getNextWizardStepAtom);
  const [handleCompleteBooking, isCompletingBooking] =
    useCompleteBooking(onClose);
  const bookingWizardData = useAtomValue(BookingWizardDataAtom);
  const trackEvent = useTrackEvent();
  const setIsCreatingBooking = useSetAtom(isCreatingBookingAtom);

  const isFinalStep = useAtomValue(isFinalStepAtom);

  const handleWizardContinueOrComplete = useCallback(() => {
    if (isFinalStep) {
      setIsCreatingBooking(true);
      trackEvent("BookingWizard.CreateDraftBooking", {
        bookingWizardData: bookingWizardData ?? null,
      });
      handleCompleteBooking();
    } else {
      if (nextStep) {
        trackEvent("BookingWizard.PressContinue", {
          nextStep: nextStep,
          bookingWizardData: bookingWizardData ?? null,
        });
      }
      navigateToNextStep();
    }
  }, [
    isFinalStep,
    bookingWizardData,
    handleCompleteBooking,
    nextStep,
    navigateToNextStep,
    trackEvent,
    setIsCreatingBooking,
  ]);

  return { handleWizardContinueOrComplete, isCompletingBooking };
};
